// Every word on the 1-2-1's pages (ClientSpace.dc.html, both Tweaks), in 1 place. The board's own
// words are as it draws them, with the client's name where the board has Laura's. Lines no board
// draws are marked "undrawn" and listed for Adam's stage 4 batch. Pure: no imports.
//
// Writing rules: British English, numerals, no exclamation marks, no em or en dashes, straight
// quotes, sentence case, contractions.

export const COPY = {
  // the page head
  h1: 'Your 1-2-1 with Adam',
  eyebrow: (name, since) => `${name}, since ${since}`,
  // the sub-pages the menu opens (undrawn: the board draws Home only)
  pageH1: { sessions: 'Sessions', recordings: 'Recordings', resources: 'Resources', actions: 'Actions' },

  // Adam's bar. The board's "She sees" becomes "They see": the hub doesn't know a client's pronoun.
  bar: first => `You're on ${first}'s page as admin. They see all of it except the edit boxes.`,
  barEnded: (first, when) => `You're on ${first}'s page as admin. The space ended on ${when}, so ${first} can't open it any more.`, // undrawn
  backToPeople: 'Back to People',

  // Next session
  nextLabel: 'Next session',
  nextOnZoom: title => `${title}, on Zoom`,
  joinOpensAt: time => `Join opens at ${time}`,
  join: 'Join', // undrawn here (the Library's, stage 3 item 60)
  joinAria: (product, title) => `Join: ${product}, ${title}`,
  addCal: 'Add to calendar',
  addedCal: 'Added to your calendar',
  notBooked: 'Not booked yet', // undrawn, Adam's view only
  notBookedLine: 'Add the date, time and Zoom link below and it shows on their page straight away.', // undrawn

  // Adam's next session box (undrawn: the critic's settlement, sign-off question 5)
  nextBox: 'Next session, Adam only',
  nextDate: 'Date',
  nextTime: 'Time, UK',
  nextLength: 'Length',
  nextZoom: 'Zoom link',
  nextAdd: 'Add the session',
  nextSave: 'Save changes',
  nextNeedWhen: 'Pick a date and a time.',
  nextPast: 'That time has gone. Pick a date and time still to come.',
  nextBadZoom: 'The Zoom link has to be a whole address starting with https://.',
  nextSaved: first => `Saved. It's on ${first}'s page now.`,

  // Book a session
  bookLabel: 'Book a session',
  bookLine: "Pick the next time that suits you from Adam's calendar.",
  bookButton: 'Book a session',
  bookNote: "Opens Adam's Calendly in a new tab, with your name and email filled in.",
  bookingBox: 'Booking link, Adam only',
  bookingSaved: 'Saved. Book a session opens this link now.', // undrawn
  bookingBad: 'The booking link has to be a whole address starting with https://.', // client-space's own line

  // Your actions
  actionsH2: 'Your actions',
  agreedAt: (n, date) => `Agreed at session ${n}, ${date}`,
  actionCount: (done, all) => `${done} of ${all} done`,
  actionsFoot: 'Tick them off as you go. Adam reads them before your next session.',
  newActionAria: 'New action',
  newActionPlaceholder: n => `Add an action for session ${n}`,
  addAction: 'Add action',
  noActions: 'Adam adds your actions after each session.', // undrawn

  // Sessions
  sessionsH2: 'Sessions',
  watch: 'Watch',
  playing: 'Playing',
  notes: 'Notes',
  // where the client is in a session's recording, as every other recording says it (stage4-for-adam 14)
  watched: 'Watched', // undrawn
  carryOnFrom: at => `Carry on from ${at}`, // undrawn
  hideNotes: 'Hide notes',
  adamsNotes: "Adam's notes",
  noNotes: 'Adam has added no notes for this session.', // undrawn
  noSessions: "Your sessions show here once you've had your first one with Adam.", // undrawn
  noRecordings: 'Each session shows here once Adam adds its recording.', // undrawn
  afterBox: n => `After session ${n}, Adam only`,
  afterLinkAria: n => `Vimeo link for session ${n}`,
  afterLinkPlaceholder: 'Vimeo link or embed code',
  afterNotesAria: n => `Notes for session ${n}`,
  afterNotesPlaceholder: first => `Your notes for ${first}, in a paragraph or 2`,
  afterSave: first => `Save and show ${first}`,
  afterSaved: (first, n) => `Saved. ${first} sees session ${n}, with your notes, straight away.`,
  afterNeedLink: 'Paste the Vimeo link first.',
  afterBadLink: "That isn't a Vimeo link. Paste the video's link from Vimeo, or its embed code.", // undrawn

  // Shared files
  filesH2: 'Shared files',
  addFile: 'Add a file',
  addFileFor: first => `Add a file for ${first}`,
  added: 'Added. Add another',
  adding: 'Adding the file', // undrawn
  download: 'Download',
  downloadAria: name => `Download ${name}`,
  fileFrom: (who, when) => `From ${who}, ${when}`,
  today: 'today',
  filesFoot: 'Only you and Adam can see these. Files are kept privately and every download link expires after a few minutes.',
  noFiles: 'No files yet.', // undrawn
  // sign-upload's own lines, checked here first so a file that can't go is never sent
  badType: "That kind of file can't be added. Add a PDF, a Word, Excel or PowerPoint file, or a photo (JPEG, PNG or WebP).",
  tooBig: 'That file is bigger than 25 MB. Add a smaller one.',
  notAdded: "The file couldn't be added just now. Try again in a minute.",

  // Ask Adam
  askH2: 'Ask Adam',
  askLine: "Anything that can't wait for your next session. It reaches Adam with a link to this page.",
  askOpen: 'Ask Adam a question',
  askAria: 'Your question',
  askPlaceholder: 'What do you want to ask?',
  askSend: 'Send to Adam',
  askSent: 'Sent. Adam has it with a link to this page.',
  askAdamView: 'Only the client can send a question from here.', // undrawn, the disabled button's title in Adam's view

  // Ending a space (undrawn: BUILD-PLAN stage 4, AD-03; owner only)
  endBox: 'End the space, Adam only',
  endLine: first => `Once it's ended, ${first} can't open the space, its sessions, notes or files any more. You can still see everything in it, and opening a new space later starts it empty.`,
  endButton: 'End the space',
  endConfirm: name => `End ${name}'s 1-2-1 space now?`,
  endYes: 'Yes, end it',
  endNo: 'Keep it open',

  // Adam's view with no space picked, and the list under /admin (undrawn)
  spacesEyebrow: 'Adam only',
  spacesH1: '1-2-1 spaces',
  spacesNone: 'No 1-2-1 spaces yet.',
  spaceOpenSince: date => `Open since ${date}`,
  spaceEnded: date => `Ended ${date}`,
  openSpaceLink: first => `Open ${first}'s space`,
  openTheirSpace: 'Open their space', // undrawn, after "They already have a 1-2-1 space open."
  noSpace: "There's no 1-2-1 space with that id.",
  toAdmin: 'Go to Admin',

  // Opening a space under /admin (undrawn; owner only, until AdminPerson takes it over at stage 5)
  openH2: 'Open a 1-2-1 space',
  openLine: "The space opens straight away. They log in at the hub with a code sent to their email, and nothing is emailed to them now.",
  openEmail: 'Their email',
  openName: 'Their full name',
  openNameHint: "Only if they're new to the hub. Someone already in it keeps the name they have.",
  openLink: 'Booking link',
  openLinkHint: 'Leave it blank for your Calendly link, calendly.com/adamsleeproperty/1-2-1-mentorship-session.',
  openButton: 'Open the space',
  opening: 'Opening the space', // undrawn

  // failures, as the frame and stage 3 have them
  notSaved: "That didn't save. Check your connection and try again.",
  notSent: "That didn't send. Check your connection and try again.",
  failedH: "That didn't load",
  failedLine: help => `Check your connection and try again. If it keeps happening, email ${help}.`,
  tryAgain: 'Try again',
}
